import React, { useState, useEffect } from 'react';
import { get } from 'lodash';
import styled from 'styled-components';
import { withBreakpoints } from 'react-breakpoints';
import html2canvas from 'html2canvas';
import { connect } from 'react-redux';
import { Grid, Row, Col, Alert } from 'react-bootstrap';
import { sum } from 'simple-statistics';
import CSVReader from 'react-csv-reader';
import { LoadingTile } from 'components/molecules/ErrorTile';
import ButtonIconSecondary from 'components/molecules/buttons/ButtonIconSecondary';
import ButtonIconPrimary from 'components/molecules/buttons/ButtonIconPrimary';
import ContentTile from 'components/atoms/layout/ContentTile';
import Overlay from 'components/atoms/layout/Overlay';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import CrossChannelLift from 'components/molecules/charts/labs/CrossChannelLift';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { fetchEntityATVLabData, refreshEntityATVLabData, updateEntityATVLabCSV, fetchMeta } from 'services/entities/duck';
import CrossChannelDataTable from 'components/molecules/tables/labs/CrossChannelDataTable';
import helpers from 'helpers';
import { getPointWidth } from 'helpers/labs/charts';
import TTDLogo from 'assets/ttd-logo.png';
import CrossChannelGlossary from 'constants/glossaries/CrossChannelGlossary';
import UpdateBlock from 'components/molecules/labs/crossChannel/UpdateBlock';
import TrackedComponentEnhancer from 'hoc/TrackedComponent';

const TrackedShareButton = TrackedComponentEnhancer({
    trackedName: 'Multi-Channel-Lift-Share-Button-Click',
    targetComponent: ButtonIconPrimary,
    sendToMixPanel: true
});


const csvReaderParseOptions = {
    header: true,
};


const CopyPadding = styled.div`
    padding: 10px 10px 10px 15px;
`;

const createPercentage = (portion, total) => {
    const percentage = parseFloat(
        (helpers.safeDivide(portion, total) * 100).toFixed(4)
    );
    return percentage;
};

const getRawCrossChannelData = (crossChannelData) => {
    const refreshData = get(crossChannelData, 'refresh', false);
    return {
        refreshData,
        initialLoad: crossChannelData.hasData === undefined && !refreshData,
        rows: get(crossChannelData, 'rows', []),
        lastExecution: get(crossChannelData, 'lastExecution', 'N/A'),
    };
};

const getHouseholdConversionRates = (inputChannels) => {
    const users = sum(inputChannels.map(r => +r.households));
    const converters = sum(inputChannels.map(r => +r.converters));

    return createPercentage(converters, users);
};

const LayerThreeLabsCrossChannelContainer = ({
    meta,
    currentBreakpoint,
    crossChannelData = {},
    refreshEntityATVLabData: resetATVData,
    fetchEntityATVLabData: fetchATVData,
    fetchMeta: fetchEntityMeta,
    updateEntityATVLabCSV: updateEntityLab
}) => {
    const { workOrders = [], id } = meta;
    const { refreshData, initialLoad, rows, lastExecution } = getRawCrossChannelData(crossChannelData);
    let currentBreakpointWidth = getPointWidth(currentBreakpoint);
    const [loading, setLoading] = useState(initialLoad);
    const [mountModal, setMountModal] = useState(false);
    const [CSVData, setCSVData] = useState([]);
    const hasData = CSVData.length || rows.length || get(crossChannelData, 'hasData', false);
    const resetData = () => {
        setCSVData([]);
        return resetATVData();
    };

    const exportLiftChart = () => {
        setMountModal(true);
    };

    useEffect(() => {
        if (mountModal) {
            html2canvas(document.querySelector('#MultiChannelLiftChartForSharing'), {
                scrollY: -window.scrollY, // ensure the full image is captured
                scale: 2
            }).then((canvas) => {
                const imgDataUrl = canvas.toDataURL('image/png');
                document.querySelector('#ShareOverlay img').src = imgDataUrl;
            });
        }
    }, [mountModal]);

    useEffect(() => {
        currentBreakpointWidth = getPointWidth(currentBreakpoint);
        setLoading(true);
        if (initialLoad) {
            fetchATVData({
                entityId: parseInt(id, 10),
                transactionId: `entity-${id}-atv-lab-data`,
                refresh: false
            });
        } else if (refreshData) {
            fetchATVData({
                entityId: parseInt(id, 10),
                transactionId: `entity-${id}-atv-lab-data-refresh`,
                refresh: true
            });
        } else {
            setLoading(false);
        }
    }, [initialLoad, refreshData]);

    useEffect(() => {
        // Paulo: what is this for?
        fetchEntityMeta({
            transactionId: `entity-${id}-summary`,
            entityId: id,
        });

        if (CSVData.length) {
            updateEntityLab({
                data: { data: CSVData },
                entityId: parseInt(id, 10),
                transactionId: `entity-${id}-summary`,
                name: meta.name,
            });
        }
    }, [CSVData]);

    const opps = workOrders.filter(r => r.salesforceId);
    const oppIds = opps.map(v => `'${v.salesforceId}'`).join(',') || "''";

    const UploadSection = <ContentTile padding="20px">
        <p style={{ fontSize: '18px', fontWeight: 'bold' }}>
            {`${(CSVData.length || crossChannelData.length)
                ? 'Having trouble refreshing the data?'
                : 'Having trouble loading the data?'
            }`}
        </p>
        <ol>
            <li>
                Open{' '}
                <a target="_blank"
                    href={`https://gw-data.goodwaygroup.com/queries/741?p_opportunity_ids%20"(%27abc%27,%27abc%27)"=(${oppIds})`}>
                    this query in Redash
                </a>
            </li>
            <li>Export the query results as a CSV</li>
            <li>Upload the CSV below</li>
        </ol>
        <br />
        <CSVReader
            parserOptions={csvReaderParseOptions}
            onFileLoaded={data => setCSVData(data)}
        />
    </ContentTile>;

    const rawData = (CSVData.length ? CSVData : rows).filter(row => row.channel_count);

    const leftHandChannels = rawData.filter(row => row.channel_type === 'single-channel');
    const rightHandChannels = rawData.filter(row => row.channel_type === 'multi-channel');

    const leftHandConversionRate = getHouseholdConversionRates(leftHandChannels);
    const rightHandConversionRate = getHouseholdConversionRates(rightHandChannels);

    const liftPercentage = Math.ceil(
        createPercentage(
            rightHandConversionRate - leftHandConversionRate,
            leftHandConversionRate
        )
    );

    return (
        (CSVData.length === 0 && crossChannelData.length === 0) ?
            <Grid fluid>
                <ChannelTitle>
                    The Power of Multiple Channels
                </ChannelTitle>
                <Row>
                    <Col sm={12}>
                        {UploadSection}
                    </Col>
                </Row>
            </Grid>
            :
            <Grid fluid>
                <Row>
                    <Col sm={10} lg={8}>
                        <ChannelTitle>
                            The Power of Multiple Channels
                        </ChannelTitle>
                    </Col>
                </Row>
                {loading &&
                    <Row>
                        <Col sm={12} lg={8}>
                            <LoadingTile>
                                We are currently gathering the data needed.
                                This could take a few seconds to a couple of minutes.
                                How about a fresh cup of coffee?
                            </LoadingTile>
                        </Col>
                    </Row>
                }
                {hasData &&
                    <Row>
                        <Col sm={12} lg={8}>
                            <React.Fragment>
                                <div
                                    id="MultiChannelLiftChartForSharing"
                                    style={{
                                        padding: mountModal ? '20px 20px 5px 10px' : 0
                                    }}
                                >
                                    <CrossChannelLift
                                        title={'MULTI-CHANNEL LIFT'}
                                        rightHandConversionRate={rightHandConversionRate}
                                        leftHandConversionRate={leftHandConversionRate}
                                        liftPercentage={liftPercentage}
                                        hasData={rightHandChannels.length}
                                        data={rawData}
                                        animation={!mountModal}
                                    />
                                </div>
                                <CrossChannelDataTable data={rawData} />
                                {Boolean(lastExecution || rawData.length) &&
                                    <UpdateBlock lastExecution={lastExecution} onClick={resetData} />
                                }
                            </React.Fragment>
                        </Col>
                        <Col sm={12} lg={4}>
                            <ContentTile style={{ marginBottom: 10 }}>
                                <ContentHeader hideCSVExport={true}>Glossary</ContentHeader>
                                <CopyPadding>
                                    <ul style={{ listStyleType: 'none', padding: '0px' }}>
                                        {CrossChannelGlossary.map((key, i) =>
                                            <li key={i} style={{ marginBottom: '10px' }}><b>{key.displayName}</b> - {key.definition}</li>
                                        )}
                                    </ul>
                                </CopyPadding>
                            </ContentTile>
                        </Col>
                        {
                            !!liftPercentage && (currentBreakpointWidth > 200) && // hide share ability on mobile and when no lift
                            <Col sm={12} lg={4}>
                                <div
                                    style={{ textAlign: 'left', padding: 5 }} // left margin matches up with ContentTile defaults
                                >
                                    <TrackedShareButton
                                        icon="share"
                                        iconColor="#fff"
                                        onClick={exportLiftChart}
                                        payload={{
                                            liftPercentage
                                        }}
                                    >
                                        {'Share Results'}
                                    </TrackedShareButton>
                                </div>
                            </Col>
                        }
                    </Row>
                }
                {
                    (!hasData && !loading) &&
                    <Row>
                        <Col sm={12} lg={8}>
                            <Alert bsStyle="warning">
                                <strong>No Cross Channel Data Found</strong><br />
                       We could not detect any cross channel data. This lab looks at the last 100 days of data.
                                <ul>
                                    <li>If you just started running your campaign, please check back in a week.</li>
                                    <li>If this is an older campaign,
                                you might not have any data to query for the last 100 days.</li>
                                </ul>
                            </Alert>
                            {lastExecution &&
                                <UpdateBlock lastExecution={lastExecution} onClick={resetData} />
                            }
                        </Col>
                    </Row>
                }
                <Row>
                    <Col sm={12} lg={8}>
                        <ContentTile>
                            <ContentHeader hideCSVExport={true}>About this Lab</ContentHeader>
                            <CopyPadding>The goal of this lab is to highlight that households are
                            much more likely to convert after being exposed to a brand message
                            on multiple channels.
                            <br />
                            <br />

                            <em>Based on conversion data provided by:</em>
                            <br />
                            <img src={TTDLogo} style={{ width: '23px', height: '23px', margin: 0 }} /><b>The Trade Desk</b>
                            </CopyPadding>
                        </ContentTile>
                    </Col>

                    <Col sm={12} lg={8}>
                        {UploadSection}
                    </Col>

                </Row>
                <Overlay
                    id="ShareOverlay"
                    mountModal={mountModal}
                    style={{ paddingTop: 200 }}
                    onClick={() => setMountModal(false)}
                >
                    <div
                        onClick={e => e.stopPropagation()} // prevent closure of modal when clicked
                        style={{
                            width: window.innerWidth * 0.6,
                            margin: ' auto'
                        }}>
                        <h2 style={{ color: '#fff' }}>Right click the image below to copy or save:</h2>
                        <img style={{ maxWidth: '100%', height: 'auto' }} />
                        <div style={{ display: 'block', paddingTop: 5 }}>
                            <ButtonIconSecondary
                                icon="close-circle"
                                iconColor="#007DC6"
                                onClick={() => {
                                    setMountModal(false);
                                }}
                            >
                                {'Close'}
                            </ButtonIconSecondary>
                        </div>
                    </div>
                </Overlay>
            </Grid >


    );
};

const mapStateToProps = state => ({
    theme: state.theme,
    meta: get(state, 'entities.meta', {}),
    crossChannelData: get(state, 'entities.meta.meta.labs.crossChannelData'),
});

export default connect(mapStateToProps, {
    refreshEntityATVLabData,
    updateEntityATVLabCSV,
    fetchEntityATVLabData,
    fetchMeta
})(withBreakpoints(LayerThreeLabsCrossChannelContainer));



// WEBPACK FOOTER //
// ./src/containers/labs/LabsCrossChannelContainer.js